/**
 * MEMBRANE-lite — the mridangam voice.
 *
 * Transferred from Chirp/flameback.html, the MEMBRANE module. Kept whole,
 * because it is small and the physics is the point: modal synthesis (a bank of
 * sine modes with per-mode decays) plus one band-passed noise transient from a
 * SEEDED buffer, and chronological damping — a closed stroke silences what is
 * still ringing on its own head. Trimmed to the four strokes this scene plays
 * (tha / ka / dhin / thom) plus the two composites they imply.
 *
 * Determinism is a contract: mulberry32 with a fixed seed fills the one shared
 * noise buffer, and the buffer source always starts at offset 0. No
 * Math.random anywhere near audio.
 *
 * Damping is STATEFUL and depends on strikes arriving in chronological order.
 * The transport books ahead in order, so that invariant holds — keep it that way.
 */

import { midiHz } from '../common/pitch';
import { mulberry32 as coreMulberry32 } from '../vendor/av-core.mjs';
import type { StrokeId } from '../../music/talas';

const BASS_RATIO = 0.375; // thoppi sits at 3/8 of the treble head
const NOISE_SEED = 0xf1a3bacc;

// SINCE AV-CORE PHASE 3 (flyway intent 2026-10-04-av-core-inline): this IS
// Chirp's single-source av-core mulberry32 (../vendor/av-core.mjs). Same seed,
// same stream; edit it in Chirp av-core/rng.js.
export const mulberry32: (seed: number) => () => number = coreMulberry32;

interface StrokeDef {
  label: string;
  head?: 'treble' | 'bass';
  damps?: boolean;
  /** [ratio x head base, initial gain, decay seconds] */
  modes?: [number, number, number][];
  noise?: { f?: number; fMul?: number; q: number; dur: number; g: number };
  layers?: (StrokeId | [StrokeId, number, number])[];
}

export const STROKES: Record<StrokeId, StrokeDef> = {
  tha: {
    label: 'THA', head: 'treble', damps: true,
    modes: [[2.0, 0.4, 0.06], [3.0, 0.3, 0.05]],
    noise: { f: 1800, q: 0.9, dur: 0.01, g: 0.8 },
  },
  nam: {
    label: 'NAM', head: 'treble',
    modes: [[1.0, 0.25, 0.5], [2.0, 1.0, 1.1], [3.01, 0.45, 0.7], [4.02, 0.2, 0.45], [5.05, 0.1, 0.3]],
    noise: { fMul: 4, q: 1.0, dur: 0.008, g: 0.5 },
  },
  dhin: {
    label: 'DHIN', head: 'treble',
    modes: [[1.0, 1.0, 1.4], [2.0, 0.55, 0.9], [3.0, 0.22, 0.5], [4.0, 0.1, 0.3]],
    noise: { fMul: 2, q: 1.0, dur: 0.006, g: 0.3 },
  },
  chapu: {
    label: 'CHAPU', head: 'treble', damps: true,
    modes: [[1.0, 0.3, 0.12], [2.0, 0.5, 0.1], [3.02, 0.6, 0.09], [4.07, 0.5, 0.08], [5.3, 0.35, 0.07]],
    noise: { f: 3000, q: 0.7, dur: 0.015, g: 1.0 },
  },
  thom: {
    label: 'THOM', head: 'bass',
    modes: [[1.0, 1.0, 0.6], [1.5, 0.35, 0.35], [2.2, 0.15, 0.2]],
    noise: { fMul: 2.5, q: 0.8, dur: 0.012, g: 0.4 },
  },
  ka: {
    label: 'KA', head: 'bass', damps: true,
    modes: [[1.0, 0.5, 0.09], [1.6, 0.3, 0.07]],
    noise: { fMul: 2, q: 0.9, dur: 0.008, g: 0.5 },
  },
  dheem: { label: 'DHEEM', layers: ['dhin', 'thom'] },
  tham: { label: 'THAM', layers: ['nam', 'thom'] },
};

export const STROKE_IDS = Object.keys(STROKES) as StrokeId[];

export interface TalaEngine {
  strike(id: StrokeId, when: number, vel?: number): void;
  setSruti(rootSemitone: number, cents?: number): void;
  setLevel(v: number): void;
  dispose(): void;
}

export function createTalaEngine(
  ctx: BaseAudioContext,
  out: AudioNode,
  level = 0.8,
  sruti = { root: 2, cents: 0 },
): TalaEngine {
  let rootNow = sruti.root;
  let centsNow = sruti.cents;
  const fT = (): number => midiHz(60 + rootNow, centsNow);
  const fB = (): number => fT() * BASS_RATIO;

  const master = ctx.createGain();
  master.gain.value = level * level;
  const body = ctx.createBiquadFilter();
  body.type = 'peaking';
  body.frequency.value = fB() * 2;
  body.gain.value = 2.5;
  body.Q.value = 1.0;
  body.connect(master);
  master.connect(out);

  const noiseBuf = (() => {
    const rnd = mulberry32(NOISE_SEED);
    const buf = ctx.createBuffer(1, Math.floor(ctx.sampleRate), ctx.sampleRate);
    const ch = buf.getChannelData(0);
    for (let i = 0; i < ch.length; i++) ch[i] = rnd() * 2 - 1;
    return buf;
  })();

  const ringing: Record<'treble' | 'bass', { gain: GainNode; until: number }[]> = { treble: [], bass: [] };
  function damp(head: 'treble' | 'bass', when: number): void {
    for (const r of ringing[head]) if (r.until > when) r.gain.gain.setTargetAtTime(0.0001, when, 0.015);
    ringing[head] = [];
  }

  function strike(id: StrokeId, when: number, vel = 1): void {
    const s = STROKES[id];
    if (!s) return;
    if (s.layers) {
      s.layers.forEach((l, i) => {
        if (Array.isArray(l)) strike(l[0], when + l[1], vel * l[2]);
        else strike(l, when, vel * (i > 0 ? 0.9 : 1));
      });
      return;
    }
    const head = s.head ?? 'treble';
    const base = head === 'treble' ? fT() : fB();
    if (s.damps) damp(head, when);
    const bus = ctx.createGain();
    bus.gain.value = 1;
    bus.connect(body);
    let longest = 0;
    let firstOsc: OscillatorNode | null = null;
    for (const [ratio, g, dec] of s.modes ?? []) {
      const osc = ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.value = base * ratio;
      const mg = ctx.createGain();
      const peak = g * (0.4 + 0.6 * vel) * 0.5; // headroom over the mode sum
      mg.gain.setValueAtTime(peak, when);
      mg.gain.setTargetAtTime(0.0001, when, dec / 6.9); // tau from T60; never ramp to 0
      osc.connect(mg); mg.connect(bus);
      osc.start(when); osc.stop(when + dec + 0.1);
      if (!firstOsc) firstOsc = osc;
      if (dec > longest) longest = dec;
    }
    const n = s.noise;
    if (n) {
      const src = ctx.createBufferSource();
      src.buffer = noiseBuf;
      const bp = ctx.createBiquadFilter();
      bp.type = 'bandpass';
      bp.frequency.value = n.f ?? base * (n.fMul ?? 2);
      bp.Q.value = n.q;
      const ng = ctx.createGain();
      ng.gain.setValueAtTime(Math.max(0.0002, n.g * vel * vel * 0.7), when);
      ng.gain.exponentialRampToValueAtTime(0.0001, when + n.dur);
      src.connect(bp); bp.connect(ng); ng.connect(bus);
      src.start(when, 0); src.stop(when + n.dur + 0.05); // fixed offset 0 — deterministic
    }
    if (!s.damps) {
      // Prune the expired before pushing: a phrase of only undamped strokes
      // (dhin/nam, or thom on the bass head) never triggers damp(), so this
      // array grew without bound — a memory leak and an O(n) walk in damp().
      ringing[head] = ringing[head].filter((r) => r.until > when);
      ringing[head].push({ gain: bus, until: when + longest });
    }
    if (firstOsc) firstOsc.onended = () => {
      ringing[head] = ringing[head].filter((r) => r.gain !== bus);
      try { bus.disconnect(); } catch { /* gone */ }
    };
  }

  return {
    strike,
    setSruti(root, cents = 0) {
      rootNow = root; centsNow = cents;
      body.frequency.setTargetAtTime(fB() * 2, ctx.currentTime, 0.05);
    },
    setLevel(v) { master.gain.setTargetAtTime(v * v, ctx.currentTime, 0.02); },
    dispose() {
      master.gain.setTargetAtTime(0.0001, ctx.currentTime, 0.02); // no click on scene switch
      setTimeout(() => { try { master.disconnect(); } catch { /* gone */ } }, 120);
    },
  };
}
